import { FORM_STEPS, type FieldIssue, type FormStep } from '@mapeamento/shared';
import { byId, esc } from './lib/html';
import { hasValue } from './lib/object-path';
import { ApiRequestError, submitMapping } from './services/api-client';
import { AttachmentBag } from './state/attachment-bag';
import { DraftStore } from './state/draft-store';
import { pageCounter, renderDoneScreen, renderStepPanel, renderStepper } from './ui/views';
import { stepOfIssue, validateStep } from './validation';

const STEPS: readonly FormStep[] = FORM_STEPS;
const STEP_IDS = STEPS.map((s) => s.id);

/**
 * Controlador do formulário: liga estado (rascunho + anexos), renderização e
 * eventos do DOM. Mantém o comportamento do HTML original e acrescenta o envio à API.
 */
export class FormApp {
  private readonly $root = byId('app');
  private readonly $steps = byId('steps');
  private readonly $panel = byId('panel');
  private readonly $pg = byId('pg');
  private readonly $bar = byId('bar');
  private readonly $save = byId('save');

  private readonly store = new DraftStore(window.localStorage, (at) => this.showSaved(at));
  private readonly attachments = new AttachmentBag();
  private done: { protocolo: string } | null = null;
  private submitting = false;
  private rendered = false;

  start(): void {
    this.store.load();
    this.store.step = Math.min(Math.max(this.store.step, 0), STEPS.length - 1);
    this.bindEvents();
    this.render();
  }

  // ---------- renderização ----------

  private render(): void {
    const current = this.store.step;
    this.$steps.innerHTML = renderStepper(STEPS, current, (i) => this.isStepTouched(i));
    this.$pg.textContent = pageCounter(current, STEPS.length);
    this.$bar.style.width = `${(current / STEPS.length) * 100}%`;

    if (this.done) return this.renderDone(this.done.protocolo);

    const step = STEPS[current]!;
    this.animatePanel(
      renderStepPanel(step, current, STEPS.length, {
        store: this.store,
        attachments: this.attachments,
      }),
    );
    if (this.rendered) window.scrollTo({ top: this.$steps.offsetTop - 90, behavior: 'smooth' });
    this.rendered = true;
  }

  private renderDone(protocolo: string): void {
    this.$bar.style.width = '100%';
    const lista = this.store.get('fontes.lista');
    this.animatePanel(
      renderDoneScreen({
        protocolo,
        secretaria: String(this.store.get('pasta.nome') ?? ''),
        totalFontes: Array.isArray(lista) ? lista.length : 0,
      }),
    );
  }

  private animatePanel(html: string): void {
    this.$panel.className = 'panel';
    void this.$panel.offsetWidth; // reinicia a animação de entrada
    this.$panel.className = 'panel enter';
    this.$panel.innerHTML = html;
  }

  private isStepTouched(index: number): boolean {
    const step = STEPS[index]!;
    if (step.fields.some((f) => f.type === 'file') && this.attachments.list.length) return true;
    return step.fields.some((f) => hasValue(this.store.get(`${step.id}.${f.id}`)));
  }

  private showSaved(at: Date): void {
    const time = at.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    this.$save.innerHTML = `Rascunho salvo às <b>${time}</b>.`;
  }

  // ---------- validação e envio ----------

  /** Marca os campos com erro no painel atual e rola até o primeiro. */
  private showIssues(issues: FieldIssue[]): void {
    let first: HTMLElement | null = null;
    this.$panel.querySelectorAll<HTMLElement>('.f').forEach((el) => {
      const issue = issues.find((i) => i.path === el.dataset.path);
      el.classList.toggle('err', Boolean(issue));
      if (issue) {
        el.querySelector(':scope > .em')!.textContent = issue.message;
        first ??= el;
      }
    });
    (first as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private showAlert(message: string): void {
    const alert = this.$panel.querySelector<HTMLElement>('#alert');
    if (!alert) return;
    alert.textContent = message;
    alert.hidden = false;
    alert.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private next(): void {
    const step = STEPS[this.store.step]!;
    const issues = validateStep(step.id, this.store.data);
    if (issues.length) return this.showIssues(issues);

    if (this.store.step === STEPS.length - 1) void this.submit();
    else {
      this.store.goTo(this.store.step + 1);
      this.render();
    }
  }

  private async submit(): Promise<void> {
    // Garante que as etapas anteriores também estão válidas (o usuário pode ter pulado).
    const invalidStep = STEPS.findIndex((s) => validateStep(s.id, this.store.data).length);
    if (invalidStep !== -1)
      return this.goToIssues(validateStep(STEPS[invalidStep]!.id, this.store.data));

    if (this.submitting) return;
    this.submitting = true;
    const button = byId<HTMLButtonElement>('next');
    button.disabled = true;
    button.textContent = 'Enviando…';

    try {
      const sent = this.store.data;
      const created = await submitMapping(sent, this.attachments.list);
      this.done = { protocolo: created.protocolo };
      // Remove o rascunho salvo, mas mantém os dados em memória para a tela final e o download.
      this.store.clear();
      this.store.data = sent;
      this.render();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Enviar mapeamento';
      if (error instanceof ApiRequestError && error.details.length) this.goToIssues(error.details);
      else this.showAlert(error instanceof Error ? error.message : String(error));
    } finally {
      this.submitting = false;
    }
  }

  private goToIssues(issues: FieldIssue[]): void {
    const target = stepOfIssue(issues[0]!, STEP_IDS);
    if (target !== this.store.step) {
      this.store.goTo(target);
      this.render();
    }
    this.showIssues(issues);
  }

  // ---------- eventos ----------

  private bindEvents(): void {
    this.$root.addEventListener('input', (e) => this.onInput(e));
    this.$root.addEventListener('change', (e) => this.onFileChange(e));
    this.$root.addEventListener('click', (e) => this.onClick(e));
    window.addEventListener('pagehide', () => this.store.flush());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.store.flush();
    });
    this.$root.addEventListener('dragover', (e) => {
      const drop = (e.target as HTMLElement).closest('[data-drop]');
      if (drop) {
        e.preventDefault();
        drop.classList.add('over');
      }
    });
    this.$root.addEventListener('dragleave', (e) => {
      (e.target as HTMLElement).closest('[data-drop]')?.classList.remove('over');
    });
    this.$root.addEventListener('drop', (e) => {
      const drop = (e.target as HTMLElement).closest('[data-drop]');
      if (!drop || !e.dataTransfer) return;
      e.preventDefault();
      drop.classList.remove('over');
      this.addFiles(e.dataTransfer.files);
    });
  }

  private onInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    const field = target.closest<HTMLElement>('.f');
    if (!field) return;
    const { path, type } = field.dataset as { path: string; type: string };

    if (type === 'check') {
      const values = [...field.querySelectorAll<HTMLInputElement>('input:checked')].map(
        (i) => i.value,
      );
      this.store.set(path, values);
    } else if (type === 'radio') {
      this.store.set(path, field.querySelector<HTMLInputElement>('input:checked')?.value ?? '');
    } else if (type !== 'file' && type !== 'repeat') {
      this.store.set(path, target.value);
    }
    field.classList.remove('err');
  }

  private onFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (input.type !== 'file' || !input.files) return;
    this.addFiles(input.files);
  }

  private addFiles(files: FileList): void {
    const { rejected } = this.attachments.add(files);
    this.store.hadStaleAttachments = false;
    this.render();
    if (rejected.length) {
      const box = this.$panel.querySelector<HTMLElement>('.em-files');
      if (box) box.innerHTML = rejected.map(esc).join('<br>');
    }
  }

  private onClick(e: Event): void {
    const button = (e.target as HTMLElement).closest('button');
    if (!button) return;
    const { dataset, id } = button;

    if (dataset.go != null) {
      if (this.done) return;
      this.store.goTo(Number(dataset.go));
      this.render();
    } else if (id === 'prev') {
      this.store.goTo(this.store.step - 1);
      this.render();
    } else if (id === 'next') {
      this.next();
    } else if (dataset.add != null) {
      const path = button.closest<HTMLElement>('.rep')!.dataset.rep!;
      const items = (this.store.get(path) as unknown[] | undefined) ?? [];
      this.store.set(path, [...items, {}]);
      this.render();
    } else if (dataset.del != null) {
      const path = button.closest<HTMLElement>('.rep')!.dataset.rep!;
      const items = [...((this.store.get(path) as unknown[] | undefined) ?? [])];
      items.splice(Number(dataset.del), 1);
      this.store.set(path, items);
      this.render();
    } else if (dataset.rm != null) {
      this.attachments.remove(Number(dataset.rm));
      this.render();
    } else if (id === 'dl') {
      this.downloadCopy();
    } else if (id === 'reset') {
      if (!confirm('Apagar o rascunho e começar um novo mapeamento?')) return;
      this.store.clear();
      this.attachments.clear();
      this.done = null;
      this.render();
    }
  }

  private downloadCopy(): void {
    const payload = {
      protocolo: this.done?.protocolo,
      ...this.store.data,
      anexos: this.attachments.list.map((f) => f.name),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const slug = String(this.store.get('pasta.nome') || 'pasta')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/gi, '-')
      .toLowerCase();
    const link = document.createElement('a');
    link.href = url;
    link.download = `mapeamento-fontes-${slug}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
